import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

const ForgotPassword: FC = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = i18n.language.startsWith('fr')
      ? 'Réinitialiser le mot de passe — AidFone'
      : 'Reset password — AidFone';
  }, [i18n.language]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);

    if (resetError) {
      setError(resetError.message);
      return;
    }
    setSent(true);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4 font-body">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl border border-brand-border p-8 shadow-sm">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-emerald-500 text-3xl">
                mark_email_read
              </span>
            </div>
            <h1 className="text-xl font-display font-bold text-brand-text mb-2">
              {t('forgotPassword.sentTitle', 'Check your inbox')}
            </h1>
            <p className="text-sm text-brand-text-muted mb-3">
              {t('forgotPassword.sentBody', { email, defaultValue: 'We sent a reset link to {{email}}.' })}
            </p>
            {/* D18 — name the sender and pre-empt the spam folder. Without this
                the most common outcome is a caregiver waiting on an email that
                already arrived in junk, then trying again and invalidating the
                first link. Both details are what support ends up saying anyway. */}
            <div className="mb-6 rounded-xl bg-brand-bg-warm border border-brand-border px-4 py-3 text-left">
              <p className="text-sm text-brand-text">
                {t('forgotPassword.sender')}
              </p>
              <p className="text-sm text-brand-text-muted mt-1.5">
                {t('forgotPassword.spamHint')}
              </p>
              <p className="text-sm text-brand-text-muted mt-1.5">
                {t('forgotPassword.expiryHint')}
              </p>
            </div>
            <Link to="/login" className="text-brand-primary hover:underline text-sm">
              {t('forgotPassword.backToLogin', 'Back to sign in')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4 font-body">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <img
            src="/images/landing/aidfone-logo-horizontal.png"
            alt="AidFone"
            className="h-10"
          />
        </Link>
        <div className="bg-white rounded-2xl border border-brand-border p-8 shadow-sm">
          <h1 className="text-2xl font-display font-bold text-brand-text mb-1">
            {t('forgotPassword.title', 'Reset password')}
          </h1>
          <p className="text-sm text-brand-text-muted mb-6">
            {t('forgotPassword.subtitle', "Enter your email and we'll send you a link.")}
          </p>

          {error && (
            <div
              role="alert"
              className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text">
                {t('forgotPassword.email', 'Email')}
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={loading || !email}
              className="py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
            >
              {loading
                ? t('forgotPassword.sending', 'Sending...')
                : t('forgotPassword.sendResetLink', 'Send reset link')}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-brand-border">
            <Link to="/login" className="text-brand-primary hover:underline text-sm">
              {t('forgotPassword.backToLogin', 'Back to sign in')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
